"use client";

import { PlaceAutocompleteWrapper } from "@/components/others/place-autocomplete-wrapper";
import { AmenitiesIconGrid } from "@/components/rhf/amenities-icon-grid";
import { BudgetField as BudgetNew } from "@/components/rhf/budget-field";
import { Input } from "@/components/rhf/input";
import { PropertyTypeField } from "@/components/rhf/property-type-field";
import { Filters } from "@/components/search/filters/desktop";
import { BathroomsSection } from "@/components/search/filters/sections/bathrooms-section";
import { BedroomsSection } from "@/components/search/filters/sections/bedrooms-section";
import { useUrlFilters } from "@/hooks/use-url-filters";
import { ArrowDownUp, Bath, BedDouble, Building2, CalendarClock, Car, CheckCircle2, Clock, Droplets, FileText, Home, Layers, ListChecks, MapPin, Ratio, Ruler, SlidersHorizontal, Sofa, Sparkles, Tag, Wallet, X } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { BalconiesSection } from "./sections/balconies-section";
import { DynamicAreaSection } from "./sections/dynamic-area-section";
import { FloorsSection } from "./sections/floors-section";
import { MaxAgeSection } from "./sections/max-age-section";
import { ParkingsSection } from "./sections/parkings-section";
import { PossessionSection } from "./sections/possession-section";
import { RentalValueSection } from "./sections/rental-value-section";
import { createFormDefaults, handlePropertyTypeChange, handleStatusChange, isMaxAgeDisabled, isPlotFacingDisabled, isPossessionDisabled } from "./shared-utils";
import { useFilterHandlers } from "./use-filter-handlers";
import facingOptions from "@/info/facings";
import furnishingOptions from "@/info/furnishing-types";
import { documentsOptions } from "@/info/selectOptions/documents";
import { waterSourcesShortOptions } from "@/info/selectOptions/waterSourcesShort";
import { spaces as spacesOptions } from "@/info/spaces";
import { constructionStatus as constructionStatusOptions } from "@/info/unit/constructionStatus";
import levelOptions from "@/info/unit/levels";
import { propertyTypeOptions } from "@/info/unit/property-types";
import { cn } from "@/utils/cn";
import formikOptions from "@/utils/formikOptions";
interface MobileFiltersProps {
  isOpen: boolean;
  onClose: () => void;
  onApplyFilters?: (data: Partial<Filters>) => void;
  onResetForm?: (resetFn: () => void) => void;
}

// Icon mapping for filter section ids
const FILTER_ICONS: Record<string, React.ReactNode> = {
  location: <MapPin size={24} />,
  propertyType: <Home size={24} />,
  bedrooms: <BedDouble size={24} />,
  bathrooms: <Bath size={24} />,
  budget: <SlidersHorizontal size={24} />,
  documents: <FileText size={24} />,
  facing: <ListChecks size={24} />,
  furnishing: <Sofa size={24} />,
  rentalValue: <Wallet size={24} />,
  spaces: <Layers size={24} />,
  waterSources: <Droplets size={24} />,
  builtupArea: <Ruler size={24} />,
  floors: <Building2 size={24} />,
  balconies: <Ratio size={24} />,
  parkings: <Car size={24} />,
  status: <CheckCircle2 size={24} />,
  levelType: <ArrowDownUp size={24} />,
  amenities: <Sparkles size={24} />,
  plotFacing: <MapPin size={24} />,
  plotArea: <Tag size={24} />,
  maxAge: <Clock size={24} />,
  possession: <CalendarClock size={24} />
};
export function MobileFilters({
  isOpen,
  onClose,
  onApplyFilters,
  onResetForm
}: MobileFiltersProps) {
  // Get URL-based filters
  const {
    filters
  } = useUrlFilters();
  // Setup react-hook-form for mobile
  const form = useForm<Filters>({
    defaultValues: {
      ...createFormDefaults(),
      ...filters
    }
  });
  const {
    control,
    handleSubmit,
    reset,
    setValue
  } = form;

  // Manual dirty field tracking (same as property-filters)
  const manualDirtyFields = useRef<Record<string, boolean>>({});

  // Reset signal for fields with internal state (like BudgetField)
  const [resetSignal, setResetSignal] = useState(false);

  // Function to mark fields as manually dirty
  // IMPORTANT: Memoize this function to prevent recreating it on every render
  // which would cause infinite loops in child components like BudgetField
  const markFieldDirty = useCallback((fieldName: string) => {
    manualDirtyFields.current[fieldName] = true;
  }, []);

  // Provide reset function to parent component
  useEffect(() => {
    if (onResetForm) {
      onResetForm(() => {
        // Set reset signal to true to bypass "active input" check in child components
        setResetSignal(true);
        // Reset form to default values
        const formDefaults = createFormDefaults();
        reset(formDefaults);
        // Clear manual dirty fields
        manualDirtyFields.current = {};
      });
    }
  }, [onResetForm, reset]);

  // Sync form with URL filters when they change
  useEffect(() => {
    const formDefaults = createFormDefaults();
    reset({
      ...formDefaults,
      ...filters
    });
  }, [filters, reset]);

  // Watch status to conditionally show Max Age and Possession
  const watchedStatus = useWatch({
    control,
    name: "status"
  }) || "";

  // Use camelCase status values for consistency

  // PlotFacing logic (match desktop)
  const watchedPropertyType = useWatch({
    control,
    name: "propertyType"
  });
  const watchedPlotAreaMin = useWatch({
    control,
    name: "plotAreaMin"
  });
  const plotFacingDisabled = watchedPropertyType ? isPlotFacingDisabled(watchedPropertyType) : false;

  // Reset plotFacing and plotAreaMin if propertyType is apartment (match desktop)
  useEffect(() => {
    handlePropertyTypeChange({
      propertyType: watchedPropertyType,
      plotAreaMin: watchedPlotAreaMin,
      setValue
    });
  }, [watchedPropertyType, watchedPlotAreaMin, setValue]);

  // Effect to auto-set/clear maxAge and possession based on status (match desktop logic)
  useEffect(() => {
    handleStatusChange(watchedStatus, setValue);
  }, [watchedStatus, setValue]);
  const maxAgeDisabled = isMaxAgeDisabled(watchedStatus);
  const possessionDisabled = isPossessionDisabled(watchedStatus);

  // Helpers for safe access
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getLength = (arr: any): number => Array.isArray(arr) ? arr.length : 0;
  const getMin = (obj: {
    min?: number;
  } | undefined): number => obj?.min ?? 0;
  const getMax = (obj: {
    max?: number;
  } | undefined): number => obj?.max ?? 0;

  // Watch all fields to determine active state
  const watchedValues = useWatch({
    control
  });

  // Derived values for active state check
  const locationsLength = getLength(watchedValues.locations);
  const bedroomsMin = getMin(watchedValues.bedrooms);
  const bedroomsMax = getMax(watchedValues.bedrooms);
  const bathroomsMin = watchedValues.bathroomsMin ?? 0;
  const budgetMin = watchedValues.budget?.min ?? 0;
  const budgetMax = watchedValues.budget?.max ?? 0;
  const builtupAreaMin = watchedValues.builtupAreaMin ?? 0;
  const floorsMin = watchedValues.floors?.min ?? 0;
  const floorsMax = watchedValues.floors?.max ?? 0;
  const balconiesMin = watchedValues.balconiesMin ?? 0;
  const parkingsMin = watchedValues.parkingsMin ?? 0;
  const rentalValueMin = watchedValues.rentalValue?.min ?? 0;
  const rentalValueMax = watchedValues.rentalValue?.max ?? 0;
  const documentsLength = getLength(watchedValues.documents);
  const facingLength = getLength(watchedValues.facing);
  const furnishingLength = Boolean(watchedValues.furnishing);
  const spacesLength = getLength(watchedValues.spaces);
  const waterSourcesLength = getLength(watchedValues.waterSources);
  const levelTypeLength = getLength(watchedValues.levelType);
  const amenitiesLength = getLength(watchedValues.amenities);
  const statusLength = getLength(watchedValues.status);
  const maxAgeActive = Boolean(watchedValues.maxAge);
  const possessionActive = Boolean(watchedValues.possession);
  const plotFacingLength = getLength(watchedValues.plotFacing);
  const plotAreaMin = watchedValues.plotAreaMin ?? 0;

  // Track selected filter for mobile
  const filterSections = useMemo(() => {
    const sections = [{
      key: "location",
      label: "Location",
      icon: FILTER_ICONS["location"],
      hasValue: locationsLength > 0
    }, {
      key: "propertyType",
      label: "Type",
      icon: FILTER_ICONS["propertyType"],
      hasValue: Boolean(watchedPropertyType)
    }, {
      key: "bedrooms",
      label: "Bedrooms",
      icon: FILTER_ICONS["bedrooms"],
      hasValue: bedroomsMin > 0 || bedroomsMax > 0
    }, {
      key: "bathroomsMin",
      label: "Baths",
      icon: FILTER_ICONS["bathrooms"],
      hasValue: bathroomsMin > 0
    }, {
      key: "budget",
      label: "Budget",
      icon: FILTER_ICONS["budget"],
      hasValue: budgetMin > 0 || budgetMax > 0
    }, {
      key: "documents",
      label: "Docs",
      icon: FILTER_ICONS["documents"],
      hasValue: documentsLength > 0
    }, {
      key: "facing",
      label: "Facing",
      icon: FILTER_ICONS["facing"],
      hasValue: facingLength > 0
    }, {
      key: "furnishing",
      label: "Furnishing",
      icon: FILTER_ICONS["furnishing"],
      hasValue: furnishingLength
    }, {
      key: "rentalValue",
      label: "Rent",
      icon: FILTER_ICONS["rentalValue"],
      hasValue: rentalValueMin > 0 || rentalValueMax > 0
    }, {
      key: "spaces",
      label: "Spaces",
      icon: FILTER_ICONS["spaces"],
      hasValue: spacesLength > 0
    }, {
      key: "waterSources",
      label: "Water",
      icon: FILTER_ICONS["waterSources"],
      hasValue: waterSourcesLength > 0
    }, {
      key: "builtupAreaMin",
      label: "Builtup",
      icon: FILTER_ICONS["builtupArea"],
      hasValue: builtupAreaMin > 0
    }, {
      key: "floors",
      label: "Floors",
      icon: FILTER_ICONS["floors"],
      hasValue: floorsMin > 0 || floorsMax > 0
    }, {
      key: "balconiesMin",
      label: "Balconies",
      icon: FILTER_ICONS["balconies"],
      hasValue: balconiesMin > 0
    }, {
      key: "parkingsMin",
      label: "Parking",
      icon: FILTER_ICONS["parkings"],
      hasValue: parkingsMin > 0
    }, {
      key: "status",
      label: "Status",
      icon: FILTER_ICONS["status"],
      hasValue: statusLength > 0
    },
    // Conditionally add Max Age and Possession right after Status
    ...(maxAgeDisabled ? [] : [{
      key: "maxAge",
      label: "Max Age",
      icon: FILTER_ICONS["maxAge"],
      hasValue: maxAgeActive
    }]), ...(possessionDisabled ? [] : [{
      key: "possession",
      label: "Possession",
      icon: FILTER_ICONS["possession"],
      hasValue: possessionActive
    }]), {
      key: "levelType",
      label: "Level",
      icon: FILTER_ICONS["levelType"],
      hasValue: levelTypeLength > 0
    }, {
      key: "amenities",
      label: "Amenities",
      icon: FILTER_ICONS["amenities"],
      hasValue: amenitiesLength > 0
    }];

    // Conditionally add plotFacing and plotArea (match desktop)
    if (!plotFacingDisabled) {
      sections.push({
        key: "plotFacing",
        label: "Plot Facing",
        icon: FILTER_ICONS["plotFacing"],
        hasValue: plotFacingLength > 0
      });
      sections.push({
        key: "plotAreaMin",
        label: "Plot area (min.)",
        icon: FILTER_ICONS["plotArea"],
        hasValue: plotAreaMin > 0
      });
    }
    return sections;
  }, [maxAgeDisabled, possessionDisabled, plotFacingDisabled, locationsLength, watchedPropertyType, bedroomsMin, bedroomsMax, bathroomsMin, budgetMin, budgetMax, documentsLength, facingLength, furnishingLength, rentalValueMin, rentalValueMax, spacesLength, waterSourcesLength, builtupAreaMin, floorsMin, floorsMax, balconiesMin, parkingsMin, statusLength, maxAgeActive, possessionActive, levelTypeLength, amenitiesLength, plotFacingLength, plotAreaMin]);

  // Split sections
  const locationSection = filterSections.find(s => s.key === "location");
  const otherSections = filterSections.filter(s => s.key !== "location");
  const [selectedFilter, setSelectedFilter] = useState(otherSections[0]?.key);

  // Ensure selectedFilter is valid when filterSections change
  useEffect(() => {
    const validKeys = otherSections.map(section => section.key);
    if (selectedFilter && !validKeys.includes(selectedFilter)) {
      setSelectedFilter(otherSections[0]?.key);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otherSections]);

  // Use the common filter handlers hook
  const {
    handleApplyFilters
  } = useFilterHandlers({
    form,
    manualDirtyFields,
    onApplyFilters
  });

  // Add scroll-to-section logic for sidebar buttons
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const handleSidebarClick = (key: string) => {
    setSelectedFilter(key);
    const el = document.getElementById(`filter-${key}`);
    if (el && scrollContainerRef.current) {
      el.scrollIntoView({
        behavior: "smooth",
        block: "start"
      });
    }
  };

  // Only render if isOpen is true
  if (!isOpen) return null;
  return <form id="mobile-filters-form" onSubmit={handleSubmit(handleApplyFilters)} className="flex flex-col bg-white h-full min-h-0 relative">
			{/* Close button - Mobile only way to close modal */}
			<button type="button" className="absolute top-4 right-4 z-20 p-1 rounded-full bg-white shadow-sm hover:bg-gray-100" onClick={onClose} aria-label="Close filters">
				<X className="w-6 h-6 text-gray-700" />
			</button>

			{/* Filters Header */}
			<div className="pt-6 px-4 pb-4 flex-shrink-0">
				<h1 className="text-xl font-bold text-gray-900">Filters</h1>
			</div>

			{/* Top: Location Section (Fixed) */}
			{locationSection && <div className="mx-4 mb-4 p-4 border-b bg-red-50 flex-shrink-0 relative z-10 rounded-xl">
					<h2 className="text-lg font-semibold mb-2 text-red-900">{locationSection.label}</h2>
					<Controller name="locations" control={control} render={({
        field
      }) => <PlaceAutocompleteWrapper className={cn("w-full px-3 !bg-white border border-red-200 rounded-md text-sm shadow-sm h-10 flex items-center", "focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500", "placeholder-gray-400 text-sm")} value={field.value || []} onChange={value => {
        field.onChange(value);
        markFieldDirty("locations");
      }} showChips={true} chipsPosition="below" autoFocus={true} placeholder="Search locations or projects..." filterField={true} />} />
				</div>}

			{/* Main Layout: Row with Sidebar + Content */}
			<div className="flex-1 flex min-h-0 overflow-hidden">
				{/* Sidebar: vertical icon nav */}
				<div className="flex flex-col items-center py-4 bg-white w-20 border-r border-gray-200 h-full overflow-y-auto overflow-x-hidden custom-scrollbar flex-shrink-0">
					{otherSections.map(cat => <button key={cat.key} type="button" className={cn("flex flex-col items-center mb-6 focus:outline-none relative w-full px-1", selectedFilter === cat.key ? "text-primary-600 font-medium" : "text-gray-400 hover:text-gray-600")} onClick={() => handleSidebarClick(cat.key)}>
							<div className="relative">
								{cat.icon}
								{/* Active Indicator (Red Dot) */}
								{/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
								{(cat as any).hasValue && <div className="absolute -right-1 -top-1 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-white"></div>}
							</div>
							<span className="text-[10px] sm:text-xs mt-1 text-center leading-tight">{cat.label}</span>
							{selectedFilter === cat.key && <div className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-8 bg-primary-600 rounded-r-full"></div>}
						</button>)}
				</div>

				{/* Main content: all other filter sections */}
				<div className="flex-1 min-h-0 bg-white overflow-y-auto" ref={scrollContainerRef}>
					<div className="p-4 space-y-8">
						{otherSections.map(section => <div key={section.key} id={`filter-${section.key}`} className="mb-8">
								<h2 className="text-lg font-semibold mb-2">{section.label}</h2>
								{/* Render the correct filter component for each section */}
								{(() => {
              switch (section.key) {
                case "propertyType":
                  return <PropertyTypeField options={propertyTypeOptions} control={control} exactPropertyTypeName="subType" setValue={setValue} exactTypeRequired hideLabel markFieldDirty={markFieldDirty} />;
                case "bedrooms":
                  return <Controller name="bedrooms" control={control} render={({
                    field
                  }) => <BedroomsSection value={field.value} onChange={value => {
                    field.onChange(value);
                    markFieldDirty("bedrooms");
                  }} />} />;
                case "bathroomsMin":
                  return <Controller name="bathroomsMin" control={control} render={({
                    field
                  }) => <BathroomsSection {...field} minAllowed={1} onChange={value => {
                    field.onChange(value);
                    markFieldDirty("bathroomsMin");
                  }} />} />;
                case "budget":
                  return <BudgetNew name="budget" control={control} markFieldDirty={markFieldDirty} resetSignal={resetSignal} />;
                case "documents":
                  return <Input control={control} name="documents" itemsClassName="grid grid-cols-1 gap-2" options={formikOptions(documentsOptions)} type="checkbox" markFieldDirty={markFieldDirty} />;
                case "facing":
                  return <Input control={control} name="facing" itemsClassName="grid grid-cols-2 gap-2" options={formikOptions(facingOptions)} type="checkbox" markFieldDirty={markFieldDirty} />;
                case "furnishing":
                  return <Input control={control} name="furnishing" itemsClassName="grid grid-cols-1 gap-2" options={formikOptions(furnishingOptions)} type="radio" markFieldDirty={markFieldDirty} />;
                case "rentalValue":
                  return <Controller name="rentalValue" control={control} render={({
                    field
                  }) => <RentalValueSection value={field.value} onChange={value => {
                    field.onChange(value);
                    markFieldDirty("rentalValue");
                  }} />} />;
                case "spaces":
                  return <Input control={control} name="spaces" itemsClassName="grid grid-cols-2 gap-2" options={formikOptions(spacesOptions)} type="checkbox" markFieldDirty={markFieldDirty} />;
                case "waterSources":
                  return <Input control={control} name="waterSources" itemsClassName="grid grid-cols-2 gap-2" options={formikOptions(waterSourcesShortOptions)} type="checkbox" markFieldDirty={markFieldDirty} />;
                case "builtupAreaMin":
                  return <Controller name="builtupAreaMin" control={control} render={({
                    field
                  }) => <DynamicAreaSection {...field} onChange={value => {
                    field.onChange(value);
                    markFieldDirty("builtupAreaMin");
                  }} />} />;
                case "floors":
                  return <Controller name="floors" control={control} render={({
                    field
                  }) => <FloorsSection value={field.value} onChange={value => {
                    field.onChange(value);
                    markFieldDirty("floors");
                  }} />} />;
                case "balconiesMin":
                  return <Controller name="balconiesMin" control={control} render={({
                    field
                  }) => <BalconiesSection {...field} onChange={value => {
                    field.onChange(value);
                    markFieldDirty("balconiesMin");
                  }} />} />;
                case "parkingsMin":
                  return <Controller name="parkingsMin" control={control} render={({
                    field
                  }) => <ParkingsSection {...field} onChange={value => {
                    field.onChange(value);
                    markFieldDirty("parkingsMin");
                  }} />} />;
                case "status":
                  return <Input control={control} name="status" itemsClassName="grid grid-cols-1 gap-2" options={constructionStatusOptions} type="radio" markFieldDirty={markFieldDirty} />;
                case "maxAge":
                  return <Controller name="maxAge" control={control} render={({
                    field
                  }) => <MaxAgeSection showShortLabel value={field.value} onChange={value => {
                    field.onChange(value);
                    markFieldDirty("maxAge");
                  }} />} />;
                case "possession":
                  return <Controller name="possession" control={control} render={({
                    field
                  }) => <PossessionSection showShortLabel value={field.value} onChange={value => {
                    field.onChange(value);
                    markFieldDirty("possession");
                  }} />} />;
                case "levelType":
                  return <Input control={control} name="levelType" itemsClassName="grid grid-cols-1 gap-2" options={formikOptions(levelOptions)} type="radio" markFieldDirty={markFieldDirty} />;
                case "amenities":
                  return <AmenitiesIconGrid hideLabel allowCustom={false} control={control} markFieldDirty={markFieldDirty} />;
                case "plotFacing":
                  return <Input control={control} name="plotFacing" itemsClassName="grid grid-cols-2 gap-2" options={formikOptions(facingOptions)} type="checkbox" markFieldDirty={markFieldDirty} />;
                case "plotAreaMin":
                  return <Controller name="plotAreaMin" control={control} render={({
                    field
                  }) => <DynamicAreaSection {...field} value={field.value ?? 0} onChange={value => {
                    field.onChange(value);
                    markFieldDirty("plotAreaMin");
                  }} />} />;
                default:
                  return <div className="text-gray-500">Filter UI for &quot;{section.label}&quot; goes here.</div>;
              }
            })()}
							</div>)}
					</div>
				</div>
			</div>
		</form>;
}